import { useState } from 'react';
import { SettingsLayout } from './SettingsLayout';
import { Copy, Check } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { reportIntentEvent } from '../../lib/intent-events';
import opencodeLogo from '../../assets/logos/opencode.png';
import cursorLogo from '../../assets/logos/cursor.png';
import claudeCodeLogo from '../../assets/logos/claude-code.png';
import codexLogo from '../../assets/logos/codex.png';

/**
 * Mirrors the plugin's coding-agent stack. Logos that ship as a bare mark on
 * transparency get an opaque brand-coloured circle (they would otherwise vanish
 * against the dark UI) and sit inset at a shared size so every mark reads as
 * the same icon-in-a-circle. Logos that ship as their own tile keep a container
 * colour matching that tile.
 */
const AGENTS = [
  {
    name: 'Claude Code',
    logo: claudeCodeLogo,
    containerClass: 'bg-[#d87756]',
    imgClass: 'h-6 w-6 object-contain',
  },
  {
    name: 'Codex',
    logo: codexLogo,
    containerClass: 'bg-white',
    imgClass: 'h-6 w-6 object-contain',
  },
  {
    name: 'Cursor',
    logo: cursorLogo,
    containerClass: 'bg-[#f4f4ef]',
    imgClass: 'h-8 w-8 object-contain',
  },
  {
    name: 'OpenCode',
    logo: opencodeLogo,
    containerClass: 'bg-white/10',
    imgClass: 'h-5 w-5 object-contain',
  },
];

const EXAMPLES = [
  'Read the client brief in ./briefs/acme-corp.md and use Miles to build a landing page for their product launch.',
  "Scrape my competitor's site at example.com, then ask Miles to build me something better with a modern design.",
  'Pull the project requirements from our Linear ticket FE-234 and use them as a brief for Miles to build a new marketing site.',
  'Ask Miles to generate three design directions for the redesign, screenshot each one, and post them to #design-review in Slack for client feedback.',
  'Use Miles to build a portfolio site for a photographer, export the WordPress theme, and deploy it to my WP Engine staging environment.',
  'Use Miles to build the site, then run a Lighthouse audit on the preview and ask Miles to iterate on any performance issues before exporting the theme.',
];

export function SettingsAgentsPage() {
  const [copied, setCopied] = useState(false);
  const installPrompt = 'Read start.bymiles.ai and set up Miles';

  const handleCopy = async () => {
    // Fire-and-forget: recording intent must not delay the clipboard write.
    reportIntentEvent('agent_instructions.copied', 'dashboard-settings-agents');
    try {
      await navigator.clipboard.writeText(installPrompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API may not be available
    }
  };

  return (
    <SettingsLayout activeTab="agents">
      <div className="space-y-12 pb-8">
        {/* Page header */}
        <div>
          <h1 className="text-2xl font-semibold text-white">Agents</h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-white/55">
            Install Miles in the coding agent you already use, then ask it to
            design and build WordPress sites.
          </p>
        </div>

        {/* Install hero — the install message *is* the design */}
        <section
          aria-label="Install message"
          className="relative isolate overflow-hidden rounded-[28px] border border-white/10 bg-neutral-900"
        >
          {/* Atmospheric backdrop */}
          <div aria-hidden className="pointer-events-none absolute inset-0">
            {/* warm spotlight from top */}
            <div className="absolute left-1/2 top-0 h-[360px] w-[760px] -translate-x-1/2 -translate-y-[55%] rounded-full bg-[radial-gradient(closest-side,_rgba(255,255,255,0.12),_transparent_70%)] blur-3xl" />
            {/* faint grid pattern */}
            <div
              className="absolute inset-0 opacity-[0.035]"
              style={{
                backgroundImage:
                  'linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)',
                backgroundSize: '52px 52px',
                maskImage:
                  'radial-gradient(ellipse 80% 60% at 50% 30%, black 30%, transparent 75%)',
              }}
            />
          </div>

          {/* The hero — refined statement */}
          <div className="relative px-8 pb-12 pt-10 sm:px-12 sm:pb-14 sm:pt-12">
            {/* Oversized quote watermark anchored behind the headline */}
            <span
              aria-hidden
              className="pointer-events-none absolute block select-none font-serif font-bold leading-[0.8] text-white/[0.08]"
              style={{
                fontSize: 'clamp(110px, 13vw, 170px)',
                top: 'clamp(36px, 4.5vw, 60px)',
                left: 'clamp(18px, 2.2vw, 32px)',
              }}
            >
              &ldquo;
            </span>

            <p className="relative text-sm font-medium text-white/45">
              Send your coding agent this message:
            </p>

            <p
              className="relative mt-5 text-balance font-semibold tracking-[-0.025em] text-white"
              style={{ fontSize: 'clamp(28px, 4.4vw, 44px)', lineHeight: 1.1 }}
            >
              Read{' '}
              <span className="whitespace-nowrap underline decoration-white/40 decoration-[3px] underline-offset-[6px]">
                start.bymiles.ai
              </span>{' '}
              and set up Miles
            </p>
          </div>

          {/* Footer band: action + agents */}
          <div className="relative flex flex-wrap items-center justify-between gap-5 border-t border-white/[0.08] bg-black/30 px-8 py-5 sm:px-12 sm:py-6">
            <Button onClick={handleCopy} className="h-11 rounded-full px-5">
              {copied ? (
                <>
                  <Check className="h-4 w-4" />
                  Copied to clipboard
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4" />
                  Copy message
                </>
              )}
            </Button>

            <div className="flex items-center gap-4">
              <span className="text-sm font-medium text-white/45">
                Works on
              </span>
              <div className="flex items-center -space-x-2">
                {AGENTS.map((agent, idx) => (
                  <div
                    key={agent.name}
                    title={agent.name}
                    style={{ zIndex: AGENTS.length - idx }}
                    className={`group relative flex h-9 w-9 items-center justify-center overflow-hidden rounded-full ring-2 ring-neutral-900 transition duration-200 ease-out hover:z-20 hover:-translate-y-0.5 hover:scale-110 ${agent.containerClass || 'bg-neutral-800'}`}
                  >
                    <img
                      src={agent.logo}
                      alt={agent.name}
                      className={agent.imgClass}
                    />
                  </div>
                ))}
              </div>
              <span className="hidden text-sm text-white/40 lg:inline">
                + any app that supports skills
              </span>
            </div>
          </div>
        </section>

        {/* Once installed — prompt prompts as editorial sidebars */}
        <section>
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-white">Inspiration</h2>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-white/55">
              Use Miles with your existing skills and workflows, here are some
              examples to get you started.
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {EXAMPLES.map((example) => (
              <article
                key={example}
                className="group relative overflow-hidden rounded-2xl border border-white/[0.08] bg-white/[0.025] p-7 transition-colors duration-200 hover:border-white/[0.18] hover:bg-white/[0.04]"
              >
                <span
                  aria-hidden
                  className="pointer-events-none absolute -top-3 left-4 block select-none font-serif font-bold leading-[0.85] text-white/[0.05]"
                  style={{ fontSize: '150px' }}
                >
                  &ldquo;
                </span>
                <p className="relative text-sm leading-7 text-white/90">
                  {example}
                </p>
              </article>
            ))}
          </div>
        </section>
      </div>
    </SettingsLayout>
  );
}
